import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const PUBLIC_ROUTES = ["/", "/login", "/register"];

export default function PageLoader() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [progress, setProgress] = useState(0);
  const prevPath = useRef(location.pathname);
  const progressRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;

      if (PUBLIC_ROUTES.includes(location.pathname) || location.pathname.startsWith("/patient-register")) return;

      setProgress(0);
      setVisible(true);
      setFadeOut(false);

      let p = 0;
      clearInterval(progressRef.current);
      progressRef.current = setInterval(() => {
        p += p < 30 ? 8 : p < 70 ? 3 : 0.5;
        if (p >= 90) { clearInterval(progressRef.current); p = 90; }
        setProgress(p);
      }, 40);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        clearInterval(progressRef.current);
        setProgress(100);
        setFadeOut(true);
        setTimeout(() => { setVisible(false); setProgress(0); }, 300);
      }, 600);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      clearInterval(progressRef.current);
    };
  }, [location.pathname]);

  const showBar = progress > 0 && progress < 100;
  const showOverlay = visible;

  return (
    <>
      {/* Top progress bar - always in DOM */}
      <div
        className="fixed top-0 left-0 right-0 z-[10000] h-[3px] pointer-events-none"
        style={{ opacity: showBar ? 1 : 0, transition: "opacity 300ms" }}
        data-testid="progress-bar-container"
      >
        <div
          className="h-full bg-gradient-to-r from-teal-400 via-teal-500 to-emerald-400"
          style={{
            width: `${progress}%`,
            transition: progress === 0 ? "none" : "width 80ms linear",
            boxShadow: "0 0 8px rgba(20,184,166,0.5)",
          }}
          data-testid="progress-bar"
        />
      </div>

      {/* Center popup - always in DOM, visibility via CSS */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
        style={{
          opacity: showOverlay && !fadeOut ? 1 : 0,
          visibility: showOverlay ? "visible" : "hidden",
          background: showOverlay ? "rgba(15, 23, 42, 0.18)" : "transparent",
          backdropFilter: showOverlay ? "blur(6px)" : "none",
          transition: "opacity 300ms, visibility 300ms",
        }}
        data-testid="page-loader-overlay"
      >
        <div
          className="flex flex-col items-center gap-4 px-8 py-6 rounded-2xl bg-white/90 shadow-2xl border border-slate-100"
          style={{
            transform: showOverlay && !fadeOut ? "scale(1)" : "scale(0.95)",
            transition: "transform 300ms",
          }}
        >
          <div className="relative w-11 h-11">
            <div className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
            <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-teal-500 animate-spin" />
          </div>
          <span className="text-sm font-medium text-slate-600 tracking-wide" data-testid="page-loader-text">
            Carregando...
          </span>
        </div>
      </div>
    </>
  );
}
